/** @jsx jsx */
import { BaseButton, Icon, Table } from "@cd2/components"
import { IComponentProp, titleize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import { format } from "date-fns"
import React from "react"
import { Link } from "react-router-dom"
import { icons } from "../../constants/icons"
import { getInternalOrderFriendlyStatusName } from "../../hooks/useExportInternalOrder"
import { HoverBranchLoader } from "../../hoverLoaders/HoverBranchLoader"
import { HoverCustomerLoader } from "../../hoverLoaders/HoverCustomerLoader"
import { HoverUserLoader } from "../../hoverLoaders/HoverUserLoader"
import { buildRoute, eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import { formatPrice } from "../../utils/utils"
import { useGlobalState } from "../GlobalStateProvider"
import { UserSelectList } from "../SelectLists/UserSelectList"
import {
  BackOrderLink,
  EditedAtColumn,
  ISort,
  LoadingTable,
  MenuColumn,
  NoResultsBlock,
} from "./utils"
import { ICustomerSelect2 } from "../SelectLists/CustomerSelectList2"
import { brandColors } from "../EOSThemeProvider"

GoodsReceiptTable.allColumns = [
  "identifier" as const,
  "transferId" as const,
  "internal_branch_transfer_status" as const,
  "branch" as const,
  "customer" as const,
  "contact" as const,
  "purchaseOrderReference" as const,
  "deliveryMethod" as const,
  "totalPrice" as const,
  "takenBy" as const,
  "date" as const,
  "dispatchedDate" as const,
  "archived" as const,
]

interface IProps {
  data: IOrder[] | null
  sort: ISort
  onSortClick: (sort: ISort) => void
  columns?: (typeof GoodsReceiptTable.allColumns)[number][]
  menuActions?: IComponentProp<typeof MenuColumn, "items">
  /** Dispatched date of the order each receipt was raised for, keyed by order id */
  dispatchedDates?: Map<string, Date | null>
}

export type IOrder = IRecord<
  "orders",
  | "archived"
  | "delivery_method"
  | "identifier"
  | "pdf_generated"
  | "purchase_order_reference"
  | "processed"
  | "sage_export_status"
  | "sage_order_number"
  | "updated_at"
  | "created_at"
  | "carriage_pennies"
  | "notes"
  | "consolidated_orders_key"
> & {
  user: IRecord<"skeem_users", "name"> | null
  customer: IRecord<"customers", "name" | "default_sage_tax_rate_id" | "tax_rate">
  contact?: IRecord<"contacts", "name" | "email">
  internal_transfer_to_branch?: IRecord<"branches", "name">
  quote: IRecord<"quotes", "identifier"> | null
  delivery: IRecord<"deliveries"> | null
  childOrder: IRecord<"orders", "identifier"> | null
  parentOrder: IRecord<"orders", "identifier"> | null
  items: IRecord<"order_items", "price_pennies" | "quantity" | "discountPercent">[]
  return_request_source_rma?: IRecord<"rmas", "identifier"> | null
}

export function GoodsReceiptTable({
  data,
  sort,
  onSortClick,
  columns = GoodsReceiptTable.allColumns,
  menuActions,
  dispatchedDates,
}: IProps) {
  const { currency } = useGlobalState()

  function currentSort(field: ISort["by"]): "ASC" | "DESC" | undefined {
    if (JSON.stringify(field) !== JSON.stringify(sort.by)) {
      return undefined
    }
    return sort.direction.toUpperCase() as "ASC" | "DESC"
  }

  function handleSortClick(field: ISort["by"], defaultSortDirection: ISort["direction"] = "ASC") {
    onSortClick({
      by: field,
      direction:
        JSON.stringify(field) === JSON.stringify(sort.by)
          ? sort.direction === "ASC"
            ? "DESC"
            : "ASC"
          : defaultSortDirection || "ASC",
    })
  }

  if (!data) {
    return <LoadingTable />
  }

  if (data.length === 0) {
    return <NoResultsBlock />
  }

  const columnNodes: Record<(typeof GoodsReceiptTable.allColumns)[number], React.ReactNode> = {
    identifier: (
      <Table.Column
        heading="ID"
        sorted={currentSort("identifier")}
        onClick={() => handleSortClick("identifier", "DESC")}
      >
        {row => (
          <React.Fragment>
            <Link to={buildRoute(eRoutes["/goods-receipts/:id"], { id: row.id })}>
              <b>{row.identifier}</b>
            </Link>
            <BackOrderLink order={row} />
            <RmaLink order={row} />
          </React.Fragment>
        )}
      </Table.Column>
    ),
    transferId: (
      <Table.Column
        heading="Transfer ID"
        sorted={currentSort("barscanInternalOrderTransferId")}
        onClick={() => handleSortClick("barscanInternalOrderTransferId", "DESC")}
      >
        {row => (
          <React.Fragment>
            <b>{row.barscanInternalOrderTransferId}</b>
          </React.Fragment>
        )}
      </Table.Column>
    ),
    internal_branch_transfer_status: (
      <Table.Column
        heading="Status"
        sorted={currentSort("internal_branch_transfer_status")}
        onClick={() => handleSortClick("internal_branch_transfer_status")}
      >
        {row =>
          getInternalOrderFriendlyStatusName(row.internal_branch_transfer_status ?? "", true) ||
          titleize(row.internal_branch_transfer_status ?? "")
        }
      </Table.Column>
    ),
    branch: (
      <Table.Column
        heading="Branch"
        sorted={currentSort("branch")}
        onClick={() => handleSortClick("branch")}
      >
        {row => {
          const branch = row.branch
          return (
            <div>
              <div>{branch ? <HoverBranchLoader id={branch.id} initialData={branch} /> : null}</div>
            </div>
          )
        }}
      </Table.Column>
    ),
    customer: (
      <Table.Column
        heading="Customer"
        sorted={currentSort("customer")}
        onClick={() => handleSortClick("customer")}
      >
        {row => (
          <HoverCustomerLoader
            id={row.customer?.id}
            initialData={row.customer}
            renderInlineLink="block"
          />
        )}
      </Table.Column>
    ),
    purchaseOrderReference: (
      <Table.Column
        heading="Reference"
        sorted={currentSort("purchase_order_reference")}
        onClick={() => handleSortClick("purchase_order_reference")}
      >
        {row => row.purchase_order_reference}
      </Table.Column>
    ),
    deliveryMethod: (
      <Table.Column
        heading="Del. Method"
        sorted={currentSort("delivery_method")}
        onClick={() => handleSortClick("delivery_method")}
      >
        {row => row.delivery_method}
      </Table.Column>
    ),
    totalPrice: (
      <Table.Column heading="Total Price">
        {row => {
          const order = row as IOrder
          const { totalPricePennies } = getTotals(
            order.items,
            order.carriage_pennies,
            order.customer?.tax_rate,
          )

          return formatPrice(totalPricePennies, { isPennies: true, currency })
        }}
      </Table.Column>
    ),
    takenBy: (
      <Table.Column
        heading="Taken by"
        sorted={currentSort("takenBy")}
        onClick={() => handleSortClick("takenBy")}
      >
        {row =>
          row.user ? (
            <HoverUserLoader id={row.user.id} renderInlineLink="block" initialData={row.user} />
          ) : null
        }
      </Table.Column>
    ),
    date: (
      <EditedAtColumn
        heading="Date"
        sorted={currentSort("updated_at")}
        onClick={() => handleSortClick("updated_at", "DESC")}
        field={{ update: "updated_at", create: "created_at", display: "created_at" }}
      />
    ),
    dispatchedDate: (
      <Table.Column heading="Dispatched">
        {row => {
          const dispatchedDate = dispatchedDates?.get(row.id)
          return dispatchedDate ? format(dispatchedDate, "dd/MM/yyyy - HH:mm") : "-"
        }}
      </Table.Column>
    ),
    contact: (
      <Table.Column heading="Contact">
        {row => (
          <span>
            {row.contact?.name}
            <br />
            {row.contact?.email}
          </span>
        )}
      </Table.Column>
    ),
    archived: (
      <Table.BooleanColumn
        heading="Archived"
        field="archived"
        renderTrue={() => <Icon icon={icons.check} />}
      />
    ),
  }

  return (
    <div
      css={css`
        tr.archived {
          background: #fffbd2;
        }
      `}
    >
      <Table data={data} keyExtractor={row => row.id} rowClass={() => ""}>
        {columns.map(name => (
          <React.Fragment key={name}>{columnNodes[name]}</React.Fragment>
        ))}
        {menuActions && <MenuColumn items={menuActions} />}
      </Table>
    </div>
  )
}

export function RmaLink({
  order,
}: {
  order?: { return_request_source_rma?: { id: string; identifier: string } | null }
}) {
  if (!order?.return_request_source_rma) {
    return null
  }

  return (
    <Link
      to={buildRoute(eRoutes["/customer-support/rma/:id"], {
        id: order.return_request_source_rma.id,
      })}
      target="_blank"
      css={css`
        display: block;
        margin: 2px -3px;
        white-space: nowrap;
      `}
    >
      <BaseButton
        buttonStyle="text"
        theme={{
          padding: { topAndBottom: 1, sides: 3 },
          fontSize: 11,
          color: brandColors.night,
          hoverColor: "rgba(0,0,0,0.05)",
        }}
        renderRight={() => <Icon icon={icons.external} size={8} />}
      >
        RMA{order.return_request_source_rma.identifier}
      </BaseButton>
    </Link>
  )
}

export interface IOrderFilters {
  query: string
  productQuery: string
  sort: { by: any; direction: "ASC" | "DESC" }
  pagination: { page: number; perPage: number }
  archived?: boolean
  customer?: ICustomerSelect2
  user?: IComponentProp<typeof UserSelectList, "value">
  contact?: string
}

// TODO: this type is an any as the table on the customer page is wierd - when that gets fixed, can make this better
export function skeemSortForGoodsReceiptTable(filter: any) {
  let sort = { ...filter.sort }
  if (sort.by === "recommended") {
    sort = [
      {
        by: "created_at",
        direction: "desc",
      },
    ]
  }
  if (sort.by === "branch") {
    sort.by = { path: ["branch", "name"] }
  }
  if (sort.by === "customer") {
    sort.by = { path: ["customer", "name"] }
  }
  if (sort.by === "quote") {
    sort.by = { path: ["quote", "identifier"] }
  }
  if (sort.by === "takenBy") {
    sort.by = { path: ["user", "name"] }
  }
  return sort
}
